import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.alwaz.travel';
    const scripts: HTMLScriptElement[] = [];

    document.title = 'Privacy Policy | Alwaz Travels – UK GDPR & Data Protection';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read the Alwaz Travels Privacy Policy. We explain how we collect, use, and protect your personal data in compliance with UK GDPR, EU GDPR, and the Data Protection Act 2018.');

    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) metaRobots.setAttribute('content', 'noindex, follow');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy | Alwaz Travels',
      description: 'Alwaz Travels Privacy Policy – how we collect, use, and protect your personal data under UK GDPR and the Data Protection Act 2018.',
      url: `${siteUrl}/privacy-policy`,
      isPartOf: { '@type': 'WebSite', name: 'Alwaz Travels', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${siteUrl}/privacy-policy` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    scripts.push(script);

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
      if (metaRobots) metaRobots.setAttribute('content', 'index, follow');
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-gray-600 mb-12">
            Last Updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <div className="space-y-10">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                1. Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Alwaz Travel London, operated by <strong>Nex Pro Travels Limited</strong>, is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your information in compliance with the UK General Data Protection Regulation (UK GDPR), the EU General Data Protection Regulation (EU GDPR), and the Data Protection Act 2018.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our website and services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            {/* Data Controller */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                2. Data Controller Information
              </h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Company Name:</strong> Nex Pro Travels Limited
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Trading As:</strong> Alwaz Travel London
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Registered in:</strong> England and Wales
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Contact Email:</strong> <a href="mailto:contact@alwaz.travel" className="text-[#C9A961] hover:underline cursor-pointer">contact@alwaz.travel</a>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Contact Phone:</strong> <a href="tel:02034884455" className="text-[#C9A961] hover:underline cursor-pointer">0203 4884455</a>
                </p>
              </div>
            </section>

            {/* What Data We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                3. What Personal Data We Collect
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect and process the following categories of personal data:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>Identity Data:</strong> Full name, title, date of birth, passport details, nationality
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Contact Data:</strong> Email address, telephone number, postal address, WhatsApp number
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Financial Data:</strong> Payment card details, billing address, transaction history
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Travel Data:</strong> Booking details, travel preferences, special requirements (dietary, accessibility, medical)
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Technical Data:</strong> IP address, browser type, device information, cookies, usage data
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Marketing Data:</strong> Your preferences for receiving marketing communications, newsletter subscriptions
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Communication Data:</strong> Records of correspondence, chat transcripts, voice call recordings (with consent)
                </li>
              </ul>
            </section>

            {/* How We Use Your Data */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                4. How We Use Your Personal Data
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use your personal data for the following purposes:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>To provide travel services:</strong> Process bookings, arrange flights, accommodation, Umrah and Hajj packages, visa assistance, and relocation services
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>To communicate with you:</strong> Send booking confirmations, travel updates, customer support responses
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>To process payments:</strong> Handle transactions securely through our payment processors
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>To comply with legal obligations:</strong> Meet regulatory requirements, prevent fraud, respond to legal requests
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>To improve our services:</strong> Analyze website usage, conduct customer satisfaction surveys, develop new features
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>To send marketing communications:</strong> Share exclusive offers, travel tips, newsletters (only with your consent)
                </li>
              </ul>
            </section>

            {/* Legal Basis */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                5. Legal Basis for Processing (GDPR Article 6)
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We process your personal data under the following legal bases:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>Contract Performance:</strong> Processing necessary to fulfill our travel service contracts with you
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Legal Obligation:</strong> Compliance with UK and international travel regulations, tax laws, and anti-money laundering requirements
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Legitimate Interest:</strong> Fraud prevention, network security, business analytics, and service improvement
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Consent:</strong> Marketing communications, cookies (non-essential), and optional data collection
                </li>
              </ul>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                6. Data Retention Periods
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your personal data only for as long as necessary:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>Booking and transaction records:</strong> 7 years (UK tax and accounting requirements)
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Marketing consent records:</strong> Until you withdraw consent or 3 years of inactivity
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Website analytics and cookies:</strong> Up to 26 months
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Customer support communications:</strong> 3 years after case closure
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                After the retention period expires, we securely delete or anonymize your data.
              </p>
            </section>

            {/* Third-Party Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                7. Sharing Your Data with Third Parties
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share your personal data with trusted third parties to deliver our services:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>Airlines and travel providers:</strong> To complete flight bookings and travel arrangements
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Hotels and accommodation providers:</strong> To secure your reservations
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Payment processors:</strong> To process secure transactions (e.g., Stripe, PayPal)
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Visa and immigration services:</strong> To assist with visa applications
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Technology service providers:</strong> Website hosting, email services, CRM systems, analytics tools
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Legal and regulatory authorities:</strong> When required by law or to protect our legal rights
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                All third parties are contractually obligated to protect your data and use it only for the specified purposes.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                8. Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our website uses cookies and similar tracking technologies to enhance your browsing experience. Cookies are small text files stored on your device.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Types of cookies we use:</strong>
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>Essential cookies:</strong> Required for website functionality (e.g., session management, security)
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Analytics cookies:</strong> Help us understand how visitors use our site (e.g., Google Analytics)
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Marketing cookies:</strong> Track your activity to deliver personalized advertisements
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can manage your cookie preferences through our cookie consent banner or your browser settings. Note that disabling certain cookies may affect website functionality.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                9. Your Data Protection Rights
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Under UK GDPR and EU GDPR, you have the following rights:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right of Access:</strong> Request a copy of the personal data we hold about you
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right to Rectification:</strong> Correct inaccurate or incomplete data
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right to Erasure ('Right to be Forgotten'):</strong> Request deletion of your data in certain circumstances
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right to Restrict Processing:</strong> Limit how we use your data
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right to Object:</strong> Object to processing based on legitimate interests or for direct marketing
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Right to Lodge a Complaint:</strong> File a complaint with the Information Commissioner's Office (ICO)
                </li>
              </ul>
            </section>

            {/* How to Exercise Rights */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                10. How to Exercise Your Rights
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To exercise any of your data protection rights, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Email:</strong> <a href="mailto:contact@alwaz.travel" className="text-[#C9A961] hover:underline cursor-pointer">contact@alwaz.travel</a>
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Phone:</strong> <a href="tel:02034884455" className="text-[#C9A961] hover:underline cursor-pointer">0203 4884455</a>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>WhatsApp:</strong> <a href="https://wa.me/447362505895" target="_blank" rel="noopener noreferrer" className="text-[#C9A961] hover:underline cursor-pointer">+44 7362 505895</a>
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4">
                We will respond to your request within <strong>one month</strong> of receipt. In complex cases, we may extend this by two additional months and will inform you accordingly.
              </p>
            </section>

            {/* ICO Complaint */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                11. Right to Lodge a Complaint with the ICO
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you believe we have not handled your personal data properly, you have the right to lodge a complaint with the UK's supervisory authority:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Information Commissioner's Office (ICO)</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#C9A961] hover:underline cursor-pointer">www.ico.org.uk</a>
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Helpline: 0303 123 1113
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Address: Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF
                </p>
              </div>
            </section>

            {/* International Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                12. International Data Transfers
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Some of our service providers and travel partners are located outside the UK and European Economic Area (EEA). When we transfer your data internationally, we ensure appropriate safeguards are in place:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  <strong>Adequacy decisions:</strong> Transfers to countries deemed adequate by the UK or EU
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Standard Contractual Clauses (SCCs):</strong> Approved by the European Commission
                </li>
                <li className="text-gray-700 leading-relaxed">
                  <strong>Binding Corporate Rules:</strong> For transfers within multinational organizations
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                For more information about international transfers, please contact us.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                13. Data Security
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-gray-700 leading-relaxed">
                  SSL/TLS encryption for data transmission
                </li>
                <li className="text-gray-700 leading-relaxed">
                  Secure payment processing through PCI-DSS compliant providers
                </li>
                <li className="text-gray-700 leading-relaxed">
                  Regular security audits and vulnerability assessments
                </li>
                <li className="text-gray-700 leading-relaxed">
                  Access controls and staff training on data protection
                </li>
                <li className="text-gray-700 leading-relaxed">
                  Secure backup and disaster recovery procedures
                </li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                14. Children's Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal data from children without parental consent. If you believe we have collected data from a child, please contact us immediately.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                15. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new policy on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                16. Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Alwaz Travel London</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Operated by Nex Pro Travels Limited
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Email: <a href="mailto:contact@alwaz.travel" className="text-[#C9A961] hover:underline cursor-pointer">contact@alwaz.travel</a>
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Phone: <a href="tel:02034884455" className="text-[#C9A961] hover:underline cursor-pointer">0203 4884455</a>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  WhatsApp: <a href="https://wa.me/447362505895" target="_blank" rel="noopener noreferrer" className="text-[#C9A961] hover:underline cursor-pointer">+44 7362 505895</a>
                </p>
              </div>
            </section>
          </div>

          {/* Back to Home */}
          <div className="mt-16 text-center">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#1A1F2E] text-white rounded-full hover:bg-[#C9A961] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}